import TableCellClearIcon from '@atlaskit/icon/core/table-cell-clear';
import TableRowDeleteIcon from '@atlaskit/icon/core/table-row-delete';
import DuplicateIcon from '@atlaskit/icon-lab/core/duplicate';
import { Inline } from '@atlaskit/primitives';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { ActionComponent } from '../ActionComponent.tsx';
import { type SelectEntriesActionsAPI } from '../actions/entries/select-entries-actions.ts';
import { MoveEntries } from '../entries/MoveEntries.tsx';
import { messages } from '../messages.ts';

import { SingleEntryActions } from './SingleEntryActions.tsx';

export type SelectEntriesActionsProps = {
  actions: SelectEntriesActionsAPI;
  canEditContent: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  hasSortedFields: boolean;
  isEntryPanelActive: boolean;
  selectedEntriesCount: number;
};

export const SelectEntriesActions = ({
  actions,
  canEditContent,
  canMoveUp,
  canMoveDown,
  hasSortedFields,
  isEntryPanelActive,
  selectedEntriesCount
}: SelectEntriesActionsProps) => {
  const { formatMessage } = useIntl();

  const isReadOnly = !canEditContent;

  const {
    handleCopyEntryLink,
    handleExpandEntry,
    handleDuplicateEntries,
    handleInsertEntries,
    handleMoveEntries,
    handleClearEntries,
    handleDeleteEntries,
    handleHoverDelete,
    handleClearHoverDelete
  } = actions;
  const canMove = canMoveUp || canMoveDown;

  // These actions have the same functionality between single + multi selected entries
  const renderSharedActions = () => (
    <>
      <ActionComponent
        type="duplicate"
        message={formatMessage(messages['action-bar.single-entry-duplicate-action'])}
        iconBefore={
          <DuplicateIcon
            label={formatMessage(messages['action-bar.single-entry-duplicate-action'])}
          />
        }
        onClick={handleDuplicateEntries}
        shortcut={SHORTCUTS.DUPLICATE}
      />
      {canMove && (
        <MoveEntries
          handleMoveEntries={handleMoveEntries}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          selectedEntriesCount={selectedEntriesCount}
        />
      )}
      <ActionComponent
        type="clear-cells"
        message={formatMessage(messages['action-bar.clear-values-action'])}
        iconBefore={
          <TableCellClearIcon
            label={formatMessage(messages['action-bar.clear-values-action'])}
          />
        }
        onClick={handleClearEntries}
        shortcut={SHORTCUTS.CLEAR}
      />
      <ActionComponent
        handleMouseEnter={handleHoverDelete}
        handleMouseLeave={handleClearHoverDelete}
        type="delete-entry"
        message={formatMessage(messages['action-bar.single-entry-delete-action'])}
        iconBefore={
          <Box
            style={{
              color: token('color.background.danger.pressed')
            }}
          >
            <TableRowDeleteIcon
              label={formatMessage(messages['action-bar.single-entry-delete-action'])}
            />
          </Box>
        }
        onClick={handleDeleteEntries}
        shortcut={SHORTCUTS.DELETE}
      />
    </>
  );

  return (
    <Inline
      testId="entries-actions-container"
      spread="space-between"
      alignBlock="center"
      space="space.150"
    >
      {selectedEntriesCount === 1 && (
        <SingleEntryActions
          handleCopyEntryLink={handleCopyEntryLink}
          handleExpandEntry={handleExpandEntry}
          handleInsertEntries={handleInsertEntries}
          hasSortedFields={hasSortedFields}
          isReadOnly={isReadOnly}
          isEntryPanelActive={isEntryPanelActive}
        />
      )}
      {!isReadOnly && renderSharedActions()}
    </Inline>
  );
};
